import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Label } from '../ui/label';
import { ConfirmModal } from '../ui/ConfirmModal';
import { Plus, Edit, Trash2, ExternalLink, Check, X, Upload } from 'lucide-react';
import { getAllPartners, createPartner, updatePartner, deletePartner } from '../../services/api';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const PartnersManager = () => {
  const [partners, setPartners] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, partner: null });
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    logo: '',
    url: '',
    order: 0,
    active: true
  });

  const fetchPartners = useCallback(async () => {
    try {
      const response = await getAllPartners();
      setPartners(response.data);
    } catch (error) {
      console.error('Failed to fetch partners:', error);
    }
  }, []);

  useEffect(() => {
    fetchPartners();
  }, [fetchPartners]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPartner) {
        await updatePartner(editingPartner.id, formData);
      } else {
        await createPartner(formData);
      }
      fetchPartners();
      handleCloseDialog();
    } catch (error) {
      console.error('Failed to save partner:', error);
      alert('Failed to save partner');
    }
  };

  const handleEdit = (partner) => {
    setEditingPartner(partner);
    setFormData({
      name: partner.name,
      logo: partner.logo,
      url: partner.url,
      order: partner.order,
      active: partner.active
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      await deletePartner(deleteModal.partner.id);
      fetchPartners();
    } catch (error) {
      console.error('Failed to delete partner:', error);
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingPartner(null);
    setFormData({
      name: '',
      logo: '',
      url: '',
      order: 0,
      active: true
    });
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/api/upload`, uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });
      setFormData({ ...formData, logo: response.data.url });
    } catch (error) {
      console.error('Failed to upload logo:', error);
      alert('Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-green-400">
          Partners Manager
        </h2>
        <Button
          onClick={() => setIsDialogOpen(true)}
          className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Partner
        </Button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {partners.map((partner) => (
          <Card
            key={partner.id}
            className="bg-black/50 border-orange-500/30 hover:border-orange-500 hover:shadow-xl hover:shadow-orange-500/30 hover:-translate-y-1 overflow-hidden group p-0 transition-all duration-300"
          >
            <div className="relative aspect-square overflow-hidden">
              <img
                src={partner.logo}
                alt={partner.name}
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute top-2 right-2 z-10">
                {partner.active ? (
                  <span className="bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
                    <Check className="w-3 h-3" /> Active
                  </span>
                ) : (
                  <span className="bg-gray-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
                    <X className="w-3 h-3" /> Inactive
                  </span>
                )}
              </div>
            </div>

            <div className="p-4 space-y-3 pb-6">
              <div>
                <h3 className="text-lg font-bold text-white group-hover:text-orange-400 transition-colors duration-300">
                  {partner.name}
                </h3>
                <p className="text-sm text-gray-400">Order: {partner.order}</p>
              </div>

              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleEdit(partner)}
                  className="flex-1 border-green-500/30 hover:bg-green-500/20"
                >
                  <Edit className="w-4 h-4 mr-1" />
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setDeleteModal({ isOpen: true, partner })}
                  className="flex-1 border-red-500/30 hover:bg-red-500/20 text-red-400"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => window.open(partner.url, '_blank')}
                  className="border-orange-500/30 hover:bg-orange-500/20"
                >
                  <ExternalLink className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-black border-orange-500/50 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-green-400">
              {editingPartner ? 'Edit Partner' : 'Add New Partner'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Partner Name *</Label>
              <Input
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Mugadavanje"
                className="bg-black/50 border-orange-500/30"
              />
            </div>

            <div>
              <Label>Logo *</Label>
              <div className="space-y-2">
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  disabled={uploading}
                  className="bg-black/50 border-orange-500/30"
                />
                {uploading && <p className="text-sm text-orange-400">Uploading...</p>}
                {formData.logo && (
                  <div className="relative w-32 h-32 rounded-lg overflow-hidden border border-orange-500/30">
                    <img src={formData.logo} alt="Preview" className="w-full h-full object-cover" />
                  </div>
                )}
              </div>
            </div>

            <div>
              <Label>Website URL *</Label>
              <Input
                required
                type="url"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                placeholder="https://example.com"
                className="bg-black/50 border-orange-500/30"
              />
            </div>

            <div>
              <Label>Display Order</Label>
              <Input
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                className="bg-black/50 border-orange-500/30"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="active"
                checked={formData.active}
                onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                className="w-4 h-4"
              />
              <Label htmlFor="active">Active (visible on website)</Label>
            </div>

            <div className="flex gap-2">
              <Button type="submit" disabled={uploading} className="flex-1 bg-gradient-to-r from-orange-500 to-green-500 hover:from-orange-600 hover:to-green-600">
                {editingPartner ? 'Update Partner' : 'Create Partner'}
              </Button>
              <Button type="button" variant="outline" onClick={handleCloseDialog} className="border-gray-500/30">
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, partner: null })}
        onConfirm={handleDelete}
        title="Confirm Delete"
        message={`Are you sure you want to delete the partner "${deleteModal.partner?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
};
